import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Container from "../../../components/Container";
import { useTranslation } from "react-i18next";

export default function InquiryWrite() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 폼 상태
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [secret, setSecret] = useState(false);
  const [content, setContent] = useState("");

  // 보조 상태
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // 제한값
  const TITLE_MAX = 120;
  const AUTHOR_MAX = 40;
  const CONTENT_MAX = 5000;

  const validate = () => {
    if (!title.trim()) return t("inquiryWrite.err.needTitle");
    if (!author.trim()) return t("inquiryWrite.err.needAuthor");
    if (!content.trim()) return t("inquiryWrite.err.needContent");

    const hasEmail = !!email.trim();
    const hasPhone = !!phone.trim();
    if (!hasEmail || !hasPhone) return t("inquiryWrite.err.needContact");
    if (!hasEmail || !hasPhone) return t("inquiryWrite.err.needContact");
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return t("inquiryWrite.err.badEmail");
    if (hasPhone && !/^[0-9\-+() ]{7,20}$/.test(phone))
      return t("inquiryWrite.err.badPhone");

    if (!password.trim() || password.trim().length < 4)
      return t("inquiryWrite.err.badPassword");

    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setDone(false);

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      // 파일이 없으니 순수 JSON으로 전송
      const body = {
        title: title.trim(),
        author: author.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password: password.trim(),
        secret: secret,
        content: content.trim(),
      };

      // 👉 백엔드가 multipart/form-data를 기대한다면,
      // 아래 fetch를 FormData 방식으로 다시 바꿔도 됨.
      const res = await fetch("/api/inquiry/write", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("HTTP " + res.status);

      setDone(true);
      navigate("/inquiry", { replace: true });
    } catch (err) {
      console.error(err);
      setError(t("inquiryWrite.err.server"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="text-blue-900">
      {/* 헤더 */}
      <section className="border-b bg-white">
        <Container className="py-10">
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
            {t("inquiryWrite.title")}
          </h1>
          <p className="mt-2 text-slate-600">{t("inquiryWrite.desc")}</p>
        </Container>
      </section>

      {/* 폼 */}
      <section className="py-12">
        <Container>
          <form
            onSubmit={onSubmit}
            className="mx-auto max-w-3xl space-y-6 rounded-2xl border bg-white p-6 shadow-sm"
          >
            {/* 제목 */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-slate-800"
              >
                {t("inquiryWrite.f.title")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <input
                id="title"
                type="text"
                maxLength={TITLE_MAX}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("inquiryWrite.ph.title")}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 text-right text-[11px] text-slate-500">
                {title.length}/{TITLE_MAX}
              </div>
            </div>

            {/* 작성자 + 비밀번호 */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="author"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryWrite.f.author")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="author"
                  type="text"
                  maxLength={AUTHOR_MAX}
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder={t("inquiryWrite.ph.author")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryWrite.f.password")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("inquiryWrite.ph.password")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 연락처 */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryWrite.f.email")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("inquiryWrite.ph.email")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-slate-800"
                >
                  {t("inquiryWrite.f.phone")}
                  <span className="text-red-600">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("inquiryWrite.ph.phone")}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* 비밀글 */}
            <div className="flex items-center gap-2">
              <input
                id="secret"
                type="checkbox"
                checked={secret}
                onChange={(e) => setSecret(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="secret" className="text-sm text-slate-700">
                {t("inquiryWrite.f.secret")}
              </label>
            </div>

            {/* 내용 */}
            <div>
              <label
                htmlFor="content"
                className="block text-sm font-medium text-slate-800"
              >
                {t("inquiryWrite.f.content")}{" "}
                <span className="text-red-600">*</span>
              </label>
              <textarea
                id="content"
                rows={12}
                maxLength={CONTENT_MAX}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={t("inquiryWrite.ph.content")}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-1 text-right text-[11px] text-slate-500">
                {content.length}/{CONTENT_MAX}
              </div>
            </div>

            {/* 메시지 */}
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            {done && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                {t("inquiryWrite.done")}
              </div>
            )}

            {/* 액션 */}
            <div className="flex items-center justify-between pt-2">
              <Link
                to="/inquiry"
                className="rounded-xl px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
              >
                {t("inquiryWrite.actions.list")}
              </Link>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="rounded-xl px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
                >
                  {t("inquiryWrite.actions.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {submitting
                    ? t("inquiryWrite.actions.submitting")
                    : t("inquiryWrite.actions.submit")}
                </button>
              </div>
            </div>
          </form>
        </Container>
      </section>
    </div>
  );
}
